import {
  getPaginatedTeams,
  TeamResponse,
  usePaginatedApi,
  type PaginatedResponse,
  type TeamPageParams,
  type TeamPathParams,
} from 'api';
import {
  getMinimumSearchCharacters,
  SearchMode,
  STALE_TIME_MS,
} from 'api/search';
import usePaginationV2, {
  DEFAULT_TABLE_ITEMS_PER_PAGE,
} from 'components/hooks/usePaginationV2';
import { QUERY_KEY_ROOTS } from 'variables';

const usePaginatedTeams = ({
  searchTerm,
  searchMode,
  myTeamsOnly,
  organisationId,
  createdBy,
}: {
  searchTerm?: string;
  searchMode: SearchMode;
  myTeamsOnly: boolean;
  organisationId: string;
  createdBy?: string;
}) => {
  const isCreatedByFilterUsable = !myTeamsOnly || !!createdBy;
  const shouldGetUnfilteredResults =
    (myTeamsOnly && !searchTerm) || (!!organisationId && !searchTerm);
  const meetsSearchThreshold =
    (searchTerm?.length ?? 0) >= getMinimumSearchCharacters(searchMode);
  const enabled =
    isCreatedByFilterUsable &&
    (shouldGetUnfilteredResults || meetsSearchThreshold);
  const {
    data,
    isFetching,
    status,
    hasNextPage: hasMore,
    fetchNextPage: loadMore,
  } = usePaginatedApi<
    PaginatedResponse<TeamResponse>,
    TeamResponse,
    TeamPageParams,
    TeamPathParams
  >({
    fetchPage: getPaginatedTeams,
    queryKey: [
      QUERY_KEY_ROOTS.PAGINATED_TEAMS_DATA,
      ...(organisationId ? [organisationId] : []),
      ...(searchTerm ? [searchTerm] : []),
      ...(createdBy ? [createdBy] : []),
    ],
    enabled,
    getItemsFromPage: (p) => p.response ?? [],
    initialPageParam: undefined,
    queryParams: {
      organisationId,
      searchTerm,
      searchMode: searchMode ?? 'STARTS_WITH',
      createdBy: myTeamsOnly ? createdBy : undefined,
      includeTotal: true,
    },
    pathParams: organisationId ? { organisationId } : undefined,
    staleTime: STALE_TIME_MS,
  });
  const { items = [], totalCount = 0 } = data ?? {};
  const total = Math.max(totalCount, items.length);
  const paginator = usePaginationV2(items, DEFAULT_TABLE_ITEMS_PER_PAGE, total);

  return {
    enabled,
    items,
    isFetching,
    status: enabled ? status : 'success',
    hasMore,
    loadMore,
    totalCount: total,
    paginator,
  };
};

export default usePaginatedTeams;
